<template>
  <div>
    <!-- Loading State -->
    <LoadingIndicator v-if="loading" message="Loading pipeline data..." />

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-10">
      <p class="text-[#D53D3D] text-sm">{{ error }}</p>
      <button
        @click="fetchPipelineData"
        class="mt-4 text-sm text-[#F76734] hover:text-[#CC5F38] underline"
      >
        Retry
      </button>
    </div>

    <!-- Content -->
    <div v-else-if="pipelineData">
      <!-- Filters (expanded mode only) -->
      <div v-if="expanded" class="mb-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Scope Toggle -->
          <FilterTabs
            v-if="tabOptions.length > 1"
            v-model="portfolioFilter"
            :options="tabOptions"
            @change="fetchPipelineData"
          />

          <!-- Entity Scope Dropdown -->
          <div v-if="scopeEntityOptions.length > 0" class="flex items-center gap-2">
            <select
              v-model="selectedEntityId"
              @change="fetchPipelineData"
              class="px-3 py-1.5 text-sm border border-[#E8E9E9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#F76734]"
            >
              <option :value="null">All Entities</option>
              <optgroup v-for="type in uniqueEntityTypes" :key="type" :label="type">
                <option v-for="entity in scopeEntityOptions.filter(e => e.type === type)" :key="entity.id" :value="entity.id">
                  {{ entity.name }}
                </option>
              </optgroup>
            </select>
          </div>

          <div class="ml-auto">
            <ExportMenu :disabled="!pipelineData" :exports="[{ type: 'excel', action: exportToExcel }]" />
          </div>
        </div>

        <!-- Active Filter Chips -->
        <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mt-3">
          <span
            v-if="selectedEntityId"
            class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-[#FEE3D9] text-[#1E1F21] rounded-full"
          >
            Scope: {{ scopeEntityOptions.find(e => e.id === selectedEntityId)?.name }}
            <button @click="selectedEntityId = null; fetchPipelineData()" class="ml-1 text-[#626264] hover:text-[#1E1F21]">&times;</button>
          </span>
          <button
            @click="clearEntityFilters"
            class="text-xs text-[#626264] hover:text-[#353537] underline"
          >
            Clear all
          </button>
        </div>
      </div>
      <!-- Summary Cards -->
      <div class="flex items-stretch gap-5 mb-5 px-5">
        <!-- Total (non-interactive, not a card) -->
        <div class="shrink-0 pr-5 border-r border-[#E8E9E9] flex flex-col justify-center">
          <p class="text-4xl font-semibold text-[#1E1F21]">{{ pipelineData.total_practices }}</p>
          <p class="text-sm text-[#626264] mt-1">Total Practices</p>
        </div>

        <!-- Status Cards (interactive) -->
        <div class="flex-1 grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-4">
          <div
            v-for="status in pipelineData.statuses"
            :key="status.status"
            class="text-center p-4 rounded-lg cursor-pointer transition-shadow border"
            :class="selectedStatus === status.status
              ? 'bg-[#FEE3D9] border-[#F76734] shadow-[0_0_40px_rgba(0,0,0,0.10)]'
              : 'bg-[#FAFAFA] border-[#E8E9E9] hover:shadow-[0_0_40px_rgba(0,0,0,0.10)]'"
            @click="toggleStatus(status.status)"
          >
            <p class="text-3xl font-semibold text-[#1E1F21]">{{ status.count }}</p>
            <p class="text-sm text-[#626264] mt-2">{{ status.status }}</p>
          </div>
        </div>
      </div>

      <!-- Practice List (when a status is selected) -->
      <div v-if="selectedStatus && selectedPractices.length" class="mt-5">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-[#1E1F21]">{{ selectedStatus }} Practices</h3>
          <button
            @click="selectedStatus = null"
            class="text-sm text-[#626264] hover:text-[#353537] underline"
          >
            Clear
          </button>
        </div>
        <div class="bg-white border border-[#E8E9E9] rounded-lg overflow-hidden" style="height: 500px">
          <AgGridVue
            :theme="standardListTheme"
            style="width: 100%; height: 100%"
            :column-defs="columnDefs"
            :row-data="gridRowData"
            :default-col-def="defaultColDef"
            :grid-options="gridOptions"
            @grid-ready="onGridReady"
            @row-clicked="onRowClicked"
          />
        </div>
      </div>

      <!-- Empty State (when status selected but no practices) -->
      <div v-else-if="selectedStatus && !selectedPractices.length" class="mt-5">
        <div class="text-center py-10 bg-[#FAFAFA] border border-[#E8E9E9] rounded-lg">
          <p class="text-sm text-[#626264]">No practices with status "{{ selectedStatus }}"</p>
        </div>
      </div>
    </div>

    <!-- Empty State (no data at all) -->
    <div v-else class="text-center py-10">
      <p class="text-sm text-[#626264]">No pipeline data available</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { AgGridVue } from 'ag-grid-vue3'
import { standardListTheme } from '@/utils/common'
import { canAccessFeature } from '@/utils/access'
import { FEATURES } from '@/constants/features'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportToExcel } from '@/utils/exportArraytoXlsx.js'

export default {
  name: 'OnboardingPipeline',
  components: { AgGridVue, FilterTabs, LoadingIndicator, ExportMenu },
  props: {
    settings: {
      type: Object,
      default: () => ({})
    },
    sectionId: {
      type: String,
      default: null
    },
    expanded: {
      type: Boolean,
      default: true
    },
    fullWidth: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      pipelineData: null,
      selectedStatus: null,
      portfolioFilter: undefined,
      selectedEntityId: null,
      scopeEntityOptions: [],
      gridApi: null,
      gridOptions: {
        animateRows: true,
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalAndFilteredRowCountComponent' },
          ],
        },
      },
      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        resizable: true,
      },
      columnDefs: [
        {
          headerName: 'Practice Name',
          field: 'entity_name',
          filter: 'agTextColumnFilter',
          cellClass: 'sensitive',
          cellStyle: { color: '#F76734', fontWeight: 500, cursor: 'pointer' },
        },
        {
          headerName: 'Handoff Date',
          field: 'network_handoff_date',
          filter: 'agDateColumnFilter',
          valueFormatter: (params) => this.formatDate(params.value),
          comparator: (a, b) => new Date(a || 0) - new Date(b || 0),
        },
        {
          headerName: 'Impl. End Date',
          field: 'implementation_end_date',
          filter: 'agDateColumnFilter',
          valueFormatter: (params) => this.formatDate(params.value),
          comparator: (a, b) => new Date(a || 0) - new Date(b || 0),
        },
        {
          headerName: 'Onboarding Days',
          field: 'onboarding_days',
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          valueFormatter: (params) => params.value != null ? params.value : '—',
          cellStyle: (params) => params.value > 75 ? { color: '#D53D3D', fontWeight: 600 } : {},
        },
        {
          headerName: 'Attribution',
          field: 'attribution',
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          valueFormatter: (params) => params.value ? params.value.toLocaleString() : '0',
        },
      ],
    }
  },
  computed: {
    tabOptions() {
      return [
        ...(canAccessFeature(FEATURES.COMPONENT_PRACTICE_PARTNER_ALL)
          ? [{ value: 'all', label: 'All Practices' }]
          : []),
        {
          value: 'my',
          label: 'My Practices'
        }
      ]
    },
    selectedPractices() {
      if (!this.selectedStatus || !this.pipelineData?.statuses) return []
      const status = this.pipelineData.statuses.find(s => s.status === this.selectedStatus)
      return status?.practices || []
    },
    gridRowData() {
      return this.selectedPractices.map(p => ({
        ...p,
        onboarding_days: this.onboardingDays(p),
      }))
    },
    uniqueEntityTypes() {
      return [...new Set(this.scopeEntityOptions.map(e => e.type))]
    },
    hasActiveFilters() {
      return this.selectedEntityId !== null
    }
  },
  methods: {
    async fetchPipelineData() {
      this.loading = true
      this.error = null
      this.selectedStatus = null
      try {
        const params = {
          infotag_type: this.settings.infotag_type || 'Implementation Status',
          entity_type: this.settings.entity_type || 'Practice'
        }

        if (this.portfolioFilter === 'my') {
          const currentUser = this.$store.state.auth.current_user
          if (currentUser && currentUser.id) {
            params.portfolio_user_id = currentUser.id
          }
        }

        if (this.selectedEntityId) {
          params.scope_entity_id = this.selectedEntityId
        }

        const response = await axios.get('/api/onboarding-pipeline/summary/', { params })
        this.pipelineData = response.data
      } catch (err) {
        console.error('Failed to fetch pipeline data:', err)
        this.error = 'Failed to load data'
      } finally {
        this.loading = false
      }
    },
    toggleStatus(status) {
      this.selectedStatus = this.selectedStatus === status ? null : status
    },
    onGridReady(params) {
      this.gridApi = params.api
    },
    onRowClicked(event) {
      this.navigateToPractice(event.data.entity_id)
    },
    navigateToPractice(entityId) {
      this.$router.push(`/entity/${entityId}/`)
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      if (isNaN(d)) return '—'
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    async fetchScopeEntities() {
      try {
        const params = {
          exclude_type: 'Practice',
          has_practice_children: 'true',
        }
        if (this.portfolioFilter === 'my') {
          const currentUser = this.$store.state.auth.current_user
          if (currentUser && currentUser.id) {
            params.portfolio_user_id = currentUser.id
          }
        }
        const response = await axios.get('/api/entities/', { params })
        if (response.data) {
          this.scopeEntityOptions = response.data
            .map(entity => ({
              id: entity.id,
              name: entity.name,
              type: entity.entity_type_name
            }))
            .filter(e => e.type !== 'Contract Cohort')
            .sort((a, b) => {
              if (a.type !== b.type) return a.type.localeCompare(b.type)
              return a.name.localeCompare(b.name)
            })
        }
      } catch (error) {
        console.error('Error fetching scope entities:', error)
        this.scopeEntityOptions = []
      }
    },
    clearEntityFilters() {
      this.selectedEntityId = null
      this.fetchPipelineData()
    },
    onboardingDays(practice) {
      if (!practice.network_handoff_date) return null
      const handoff = new Date(practice.network_handoff_date)
      if (isNaN(handoff)) return null
      const end = practice.implementation_end_date ? new Date(practice.implementation_end_date) : new Date()
      if (isNaN(end)) return null
      return Math.round((end - handoff) / (1000 * 60 * 60 * 24))
    },
    exportToExcel() {
      if (!this.pipelineData?.statuses?.length) {
        console.warn('No pipeline data available to export')
        return
      }
      const rows = []
      this.pipelineData.statuses.forEach(status => {
        (status.practices || []).forEach(p => {
          rows.push({
            Status: status.status,
            'Practice Name': p.entity_name,
            'Handoff Date': this.formatDate(p.network_handoff_date),
            'Impl. End Date': this.formatDate(p.implementation_end_date),
            'Onboarding Days': this.onboardingDays(p),
            Attribution: p.attribution || 0,
          })
        })
      })
      if (!rows.length) {
        console.warn('No practices to export')
        return
      }
      const filename = `onboarding_pipeline_${new Date().toISOString().split('T')[0]}.xlsx`
      exportToExcel(rows, filename)
    }
  },
  mounted() {
    if (!this.portfolioFilter) {
      this.portfolioFilter = this.tabOptions[0].value
    }
    this.fetchPipelineData()
    this.fetchScopeEntities()
  },
  watch: {
    settings: {
      handler() {
        this.fetchPipelineData()
      },
      deep: true
    },
    portfolioFilter() {
      this.selectedEntityId = null
      this.fetchScopeEntities()
    }
  }
}
</script>
